import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CountryPhoneCode } from '@campaign-portal/namespace/entities/countries/specs';
import { CountryNetworkRef } from '@campaign-portal/namespace/entities/mccmnc/specs';
import { exist } from '@campaign-portal/namespace/common/id';
import { sortData } from '@campaign-portal/components-library';
import { CountriesNetworksService } from './countries-networks.service';

export interface CountryPhoneCodeEntity extends CountryPhoneCode {
	id: number;
	code: number;
	countries: string[];
	view: string;
}

@Injectable({
	providedIn: 'root',
})
export class CountriesService {
	readonly list$ = new BehaviorSubject<CountryPhoneCodeEntity[]>([]);
	readonly map$ = new BehaviorSubject<Map<number, CountryPhoneCodeEntity>>(new Map());

	constructor(private readonly refBook: CountriesNetworksService) {
		this.refBook.map$.pipe(takeUntilDestroyed()).subscribe((refBook) => {
			const list = this.toPhoneCodes(refBook.values());
			this.list$.next(list);
			this.map$.next(new Map(list.map((phoneCode) => [phoneCode.code, phoneCode])));
		});
	}

	get list(): CountryPhoneCodeEntity[] {
		return this.list$.getValue();
	}

	get map(): Map<number, CountryPhoneCodeEntity> {
		return this.map$.getValue();
	}

	get loading$(): BehaviorSubject<boolean> {
		return this.refBook.loading$;
	}

	private toPhoneCodes(refBook: Iterable<CountryNetworkRef<exist>>): CountryPhoneCodeEntity[] {
		const countriesByCode = new Map<number, Set<string>>();
		for (const { country, countryDialCode } of refBook) {
			if (!countryDialCode) {
				continue;
			}
			const countries = countriesByCode.get(countryDialCode) ?? new Set<string>();
			countriesByCode.set(countryDialCode, countries.add(country));
		}

		return Array.from(countriesByCode, ([code, unique]) => {
			const countries = sortData(Array.from(unique));
			const [first, ...rest] = countries;
			const shared = rest.length ? ` +${rest.length}` : '';
			return {
				id: code,
				code,
				name: countries.join(', '),
				countries,
				view: `( + ${code} ) ${first}${shared}`,
			};
		}).sort((a, b) => a.name.localeCompare(b.name));
	}
}
